import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauBuoc from "components/DauBuoc";
import { SEMANTIC, MAU, RADIUS, SPACE } from "assets/theme/avocadoBrand";
import { chuVuonApi } from "api/chuVuon";
import FormChuVuon, { RONG_CHU_VUON } from "components/FormChuVuon";
import useTour from "hooks/useTour";
import buocChonChuVuon from "tours/chonChuVuon";
import { dungScannerAnToan, tinhQrBox } from "lib/dungCameraQr";

const CAMERA_BOX_ID = "vung-quet-qr-chu-vuon";

const oThe = {
  borderRadius: `${RADIUS.card}px`,
  overflow: "hidden",
  boxShadow: "none",
  border: `1px solid ${SEMANTIC.border}`,
};

const oCheDo = {
  backgroundColor: SEMANTIC.background,
  border: `1px solid ${SEMANTIC.border}`,
  borderRadius: `${RADIUS.lg}px`,
};

const oDongKetQua = {
  borderRadius: `${RADIUS.md}px`,
  px: 1.5,
  py: 1.25,
  gap: 1,
  border: `1px solid ${SEMANTIC.border}`,
  mb: 1,
  "&:hover": { backgroundColor: SEMANTIC.background },
  "&:last-of-type": { mb: 0 },
};

function ChonChuVuon() {
  const navigate = useNavigate();

  const [dungCamera, setDungCamera] = useState(true);
  const [loiCamera, setLoiCamera] = useState("");
  const [dangKhoiDongCamera, setDangKhoiDongCamera] = useState(true);

  const [tuKhoa, setTuKhoa] = useState("");
  const [ketQua, setKetQua] = useState(null);
  const [taoMoi, setTaoMoi] = useState(RONG_CHU_VUON);
  const [dangTim, setDangTim] = useState(false);
  const [dangTao, setDangTao] = useState(false);
  const [loi, setLoi] = useState("");

  const qrRef = useRef(null);
  const dangXuLyRef = useRef(false);
  const quetGanNhatRef = useRef({ ma: null, luc: 0 });

  async function apDungKetQua(ds) {
    if (ds.length === 1) {
      await dungScannerAnToan(qrRef.current);
      qrRef.current = null;
      navigate(`/thu-mua/${ds[0].id}/vuon`);
      return;
    }
    setKetQua(ds);
  }

  async function traTheoQr(maQuetDuoc) {
    const ma = maQuetDuoc.trim();
    const gio = Date.now();
    if (quetGanNhatRef.current.ma === ma && gio - quetGanNhatRef.current.luc < 3000) return;
    quetGanNhatRef.current = { ma, luc: gio };

    if (dangXuLyRef.current) return;
    dangXuLyRef.current = true;
    setLoi("");
    try {
      const ds = await chuVuonApi.timTheoMa(ma);
      await apDungKetQua(ds);
    } catch (err) {
      setLoi(err.message);
    } finally {
      dangXuLyRef.current = false;
    }
  }

  useEffect(() => {
    if (!dungCamera) return undefined;

    let huy = false;
    const qr = new Html5Qrcode(CAMERA_BOX_ID);
    qrRef.current = qr;
    setLoiCamera("");
    setDangKhoiDongCamera(true);

    qr.start(
      { facingMode: "environment" },
      {
        fps: 10,
        // advanced/focusMode: nhiều điện thoại Android mặc định không auto-focus
        // liên tục khi mở camera qua getUserMedia, khiến mã QR in nhỏ/cầm gần
        // luôn bị mờ và không bao giờ đọc được dù camera "chạy" bình thường.
        // Trình duyệt không hỗ trợ constraint này sẽ tự bỏ qua, không lỗi.
        // (phải đặt trong videoConstraints — cameraIdOrConfig ở start() chỉ
        // cho phép đúng 1 key nên không nhét "advanced" vào đó được).
        videoConstraints: {
          facingMode: "environment",
          advanced: [{ focusMode: "continuous" }],
        },
        // Khung vuông cố định 250px có thể to hơn khung hình camera thật trên
        // màn hình hẹp/camera phân giải thấp — lúc đó thư viện không quét
        // được gì cả. Tính theo % kích thước khung hình thật để luôn vừa.
        qrbox: tinhQrBox,
        // Bộ giải mã JS thuần (zxing) đi kèm thư viện yếu hơn hẳn API quét mã
        // vạch gốc của trình duyệt (BarcodeDetector) — máy nào hỗ trợ (Chrome/
        // Edge Android phần lớn) sẽ tự chuyển sang dùng, máy không hỗ trợ vẫn
        // rơi về cách cũ, không lỗi gì thêm.
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      },
      (maQuetDuoc) => traTheoQr(maQuetDuoc),
      () => {}
    )
      .then(() => {
        if (huy) {
          // Đã rời màn hình / đổi chế độ trước khi start() kịp xong — lúc
          // cleanup có thể chạy khi start() còn bay, phải tự dừng ở đây nếu
          // không camera sẽ chạy ngầm vô thời hạn.
          dungScannerAnToan(qr);
          return;
        }
        setDangKhoiDongCamera(false);
      })
      .catch((err) => {
        if (huy) return;
        setDangKhoiDongCamera(false);
        setDungCamera(false);
        setLoiCamera(
          `Không mở được camera (${
            err?.message ?? err
          }). Hãy cấp quyền camera cho trình duyệt, hoặc tìm bằng CCCD/SĐT/mã chủ vườn bên dưới.`
        );
      });

    return () => {
      huy = true;
      // Luôn gọi dừng — không chỉ khi isScanning: start() có thể còn đang bay.
      dungScannerAnToan(qr);
      if (qrRef.current === qr) qrRef.current = null;
    };
  }, [dungCamera]);

  async function moTaoMoi() {
    await dungScannerAnToan(qrRef.current);
    qrRef.current = null;
    setLoi("");
    setKetQua([]);
    // Ẩn khung camera bằng CSS (không unmount #CAMERA_BOX_ID) để không còn
    // khoảng trống giữa khung quét và form tạo mới bên dưới.
    setDungCamera(false);
  }

  async function timKiem(e) {
    e.preventDefault();
    setLoi("");
    setDangTim(true);
    try {
      setKetQua(await chuVuonApi.timKiem(tuKhoa.trim()));
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangTim(false);
    }
  }

  async function xacNhanTaoMoi(e) {
    e.preventDefault();
    setLoi("");
    setDangTao(true);
    try {
      const chuVuon = await chuVuonApi.taoMoi(taoMoi);
      navigate(`/thu-mua/${chuVuon.id}/vuon`);
    } catch (err) {
      setLoi(err.message);
      setDangTao(false);
    }
  }

  // Hai nút chuyển chế độ chỉ có tác dụng khi ĐỔI sang chế độ còn lại — bấm
  // vào chế độ đang bật là không làm gì, để không vô tình xoá kết quả đang
  // hiển thị (trước đây mỗi chế độ chỉ hiện đúng nút chuyển sang chế độ kia).
  function chuyenSangQuetQr() {
    if (dungCamera) return;
    setLoi("");
    setKetQua(null);
    setDungCamera(true);
  }

  async function chuyenSangNhapTay() {
    if (!dungCamera) return;
    // Dừng camera trước khi ẩn UI — không unmount #CAMERA_BOX_ID (chỉ CSS),
    // tránh AbortError "play() was interrupted because the media was removed".
    await dungScannerAnToan(qrRef.current);
    qrRef.current = null;
    setLoi("");
    setDungCamera(false);
  }

  useTour("thu-mua", buocChonChuVuon());

  const hienFormTaoMoi = Array.isArray(ketQua) && ketQua.length === 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 2, md: 3 }}>
        <Grid container justifyContent="center">
          <Grid
            item
            xs={12}
            sm={11}
            md={hienFormTaoMoi ? 10 : 8}
            lg={hienFormTaoMoi ? 8 : 6}
            xl={hienFormTaoMoi ? 7 : 5}
          >
            <Card sx={{ ...oThe, mb: `${SPACE[5]}px` }}>
              <MDBox p={{ xs: 2.5, sm: 3 }}>
                <DauBuoc
                  buoc={1}
                  tieuDe="Chọn chủ vườn"
                  moTa="Quét mã QR của chủ vườn, hoặc tìm bằng CCCD / SĐT / mã chủ vườn."
                />

                <MDBox
                  id="tour-thu-mua-che-do-quet"
                  display="flex"
                  gap={0.5}
                  p={0.5}
                  mb={2.5}
                  sx={oCheDo}
                >
                  <MDButton
                    fullWidth
                    variant={dungCamera ? "gradient" : "text"}
                    color={dungCamera ? "info" : "dark"}
                    onClick={chuyenSangQuetQr}
                  >
                    <Icon sx={{ mr: 0.75 }}>qr_code_scanner</Icon>
                    Quét QR
                  </MDButton>
                  <MDButton
                    fullWidth
                    variant={dungCamera ? "text" : "gradient"}
                    color={dungCamera ? "dark" : "info"}
                    onClick={chuyenSangNhapTay}
                  >
                    <Icon sx={{ mr: 0.75 }}>keyboard</Icon>
                    Nhập tay
                  </MDButton>
                </MDBox>

                {/* Giữ #CAMERA_BOX_ID luôn trong DOM — chỉ ẩn bằng CSS khi nhập tay,
                    tránh AbortError khi <video> bị gỡ giữa lúc play()/start(). */}
                <MDBox sx={{ display: dungCamera ? "block" : "none" }}>
                  {/* Không ép aspect-ratio/max-height cho khung này: html5-qrcode tự
                      chèn <video> theo tỉ lệ camera thật, ép khung sẽ làm hình bị cắt
                      lệch với vùng quét mà thư viện vẽ lên. */}
                  <MDBox
                    id={CAMERA_BOX_ID}
                    sx={{
                      width: "100%",
                      minHeight: 250,
                      borderRadius: `${RADIUS.lg}px`,
                      overflow: "hidden",
                      backgroundColor: MAU.ink,
                      outline: `2px solid ${MAU.pit}`,
                      outlineOffset: 2,
                    }}
                  />
                  <MDBox display="flex" alignItems="center" gap={1} mt={1.5}>
                    <Icon sx={{ color: MAU.pit, fontSize: "1.1rem" }}>center_focus_strong</Icon>
                    <MDTypography variant="caption" color="text">
                      {dangKhoiDongCamera
                        ? "Đang mở camera..."
                        : "Đưa mã QR của chủ vườn vào giữa khung hình."}
                    </MDTypography>
                  </MDBox>
                </MDBox>

                {!dungCamera && (
                  <>
                    {loiCamera && (
                      <MDBox mb={2}>
                        <MDAlert color="warning">{loiCamera}</MDAlert>
                      </MDBox>
                    )}
                    <MDBox
                      component="form"
                      onSubmit={timKiem}
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap={1}
                    >
                      <MDInput
                        label="CCCD, SĐT hoặc mã chủ vườn"
                        value={tuKhoa}
                        onChange={(e) => setTuKhoa(e.target.value)}
                        autoFocus
                        fullWidth
                      />
                      <MDButton
                        type="submit"
                        variant="gradient"
                        color="info"
                        disabled={dangTim}
                        sx={{
                          flexShrink: 0,
                          minWidth: "5.5rem",
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        {dangTim ? "Đang tìm…" : "Tìm"}
                      </MDButton>
                    </MDBox>

                    {ketQua === null && !loi && (
                      <MDTypography variant="caption" color="text" display="block" mt={1.25}>
                        Mã không phân biệt hoa/thường và có thể bỏ gạch ngang.
                      </MDTypography>
                    )}
                  </>
                )}

                {loi && (dungCamera || ketQua === null) && (
                  <MDBox mt={2}>
                    <MDAlert color="error">{loi}</MDAlert>
                  </MDBox>
                )}

                {ketQua?.length > 0 && (
                  <MDBox mt={2.5}>
                    <MDTypography variant="overline" color="text">
                      Chọn chủ vườn phù hợp
                    </MDTypography>
                    <List sx={{ mt: 1, py: 0 }}>
                      {ketQua.map((cv) => (
                        <ListItemButton
                          key={cv.id}
                          onClick={() => navigate(`/thu-mua/${cv.id}/vuon`)}
                          sx={oDongKetQua}
                        >
                          <MDBox flex={1} minWidth={0}>
                            <MDTypography variant="button" fontWeight="medium" display="block">
                              {cv.hoTen}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              CCCD {cv.cccd} · {cv.sdt}
                            </MDTypography>
                          </MDBox>
                          <Icon sx={{ color: SEMANTIC.textSecondary }}>chevron_right</Icon>
                        </ListItemButton>
                      ))}
                    </List>
                  </MDBox>
                )}

                {!hienFormTaoMoi && (
                  <MDBox mt={2.5} pt={2} sx={{ borderTop: `1px solid ${SEMANTIC.border}` }}>
                    <MDButton
                      id="tour-thu-mua-them-moi"
                      fullWidth
                      variant="outlined"
                      color="info"
                      onClick={moTaoMoi}
                    >
                      <Icon sx={{ mr: 0.75 }}>person_add</Icon>
                      Thêm chủ vườn mới
                    </MDButton>
                  </MDBox>
                )}
              </MDBox>
            </Card>

            {hienFormTaoMoi && (
              <Card id="tour-thu-mua-them-moi" sx={oThe}>
                <MDBox p={{ xs: 2.5, sm: 3 }} component="form" onSubmit={xacNhanTaoMoi}>
                  <MDBox textAlign="center" mb={2.5}>
                    <Icon sx={{ color: SEMANTIC.accent, fontSize: "2.25rem" }}>person_search</Icon>
                    <MDTypography variant="h5" mt={1} mb={0.5}>
                      Thêm chủ vườn mới
                    </MDTypography>
                    <MDTypography
                      variant="button"
                      fontWeight="regular"
                      color="text"
                      display="block"
                    >
                      Không tìm thấy trong hệ thống. Điền hồ sơ — mục có dấu * là bắt buộc.
                    </MDTypography>
                  </MDBox>
                  {loi && (
                    <MDBox mb={2}>
                      <MDAlert color="error">{loi}</MDAlert>
                    </MDBox>
                  )}
                  <Grid container spacing={2}>
                    <FormChuVuon value={taoMoi} onChange={setTaoMoi} />
                  </Grid>
                  <MDBox
                    display="flex"
                    flexWrap="wrap"
                    gap={1}
                    mt={3}
                    sx={{ "& > *": { width: { xs: "100%", sm: "auto" } } }}
                  >
                    <MDButton type="submit" variant="gradient" color="info" disabled={dangTao}>
                      {dangTao ? "Đang tạo..." : "Tạo chủ vườn"}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setKetQua(null);
                        setLoi("");
                      }}
                    >
                      Huỷ
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ChonChuVuon;
